import { useId, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/cn';
import { useDemoMode } from './DemoModeContext';

// =========================================================================
// Disclosure — "there is more here, and it is still here".
//
// The simplification pass (docs/plans/simplification-pass.md §B5–B8) asks the
// executive scenes to land at roughly two viewports *without deleting
// anything*. That is a disclosure, not a cut: the operational material stays
// mounted-on-demand behind a summary row that says what is inside and how
// much of it there is.
//
// Why not a bare `<details>`/`<summary>`? Two reasons, both about the demo:
//
//   1. `<summary>` carries a UA marker and its own focus ring that neither
//      theme token can reach, and the OEM scenes are token-only.
//   2. The children must not mount until the row is opened. A closed
//      `<details>` still renders its subtree, and `/fleet`'s six mine
//      sections are ~100 sparkline cards — the whole point of B5 is to stop
//      paying for them on the default path.
//
// So: a `<button aria-expanded>` controlling a region, which is the same
// contract a native disclosure exposes to assistive tech. Space and Enter
// come free with `<button>`; Escape closes when focus is inside.
//
// Demo mode. The simplification is for the executive room, not for a working
// session (Tim, 23 Sep). So an *uncontrolled* band follows the demo mode
// until someone touches it: closed in Executive, open in Full, because Full
// mode's whole promise is the original depth. `openInFull={false}` opts a
// band out. The mode rehydrates from storage after mount, so this is derived
// on every render rather than seeded into `useState` — a Full-mode session
// would otherwise paint its bands closed and never open them. Once the reader
// toggles a band, their choice wins over the mode for as long as it is
// mounted. A *controlled* band (`/fleet`'s jump bar) is the parent's to open;
// the mode does not reach it.
//
// Fork-owned (zone C). Presentation only — no data, no derivation; the one
// thing it reads is the demo mode.
// UI layer (layer 4 of the 4-layer architecture).
// =========================================================================

export interface DisclosureProps {
  /** What is inside. Sentence case, e.g. "More cost views". */
  label: string;
  /**
   * How much is inside — rendered after a middot, e.g. "8 sections". The
   * count is the whole promise of the row: it says nothing was deleted.
   */
  count?: string;
  /** One line under the label, for when the label alone is not enough. */
  hint?: string;
  /**
   * Open on first paint in every mode. Defaults to closed — that is the
   * point, in Executive mode at least; see {@link DisclosureProps.openInFull}.
   */
  defaultOpen?: boolean;
  /**
   * Open by default when the demo mode is `full`. Defaults to `true`: Full
   * mode restores the depth the simplification pass folded away. Ignored in
   * controlled mode.
   */
  openInFull?: boolean;
  /**
   * Controlled mode. Pass both to drive the band from outside — `/fleet`'s
   * jump bar has to be able to open it before scrolling to a section inside
   * it. Omit both and the band keeps its own state.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

/**
 * A band that holds a scene's operational depth — closed by default in
 * Executive mode, open by default in Full mode.
 *
 * ```tsx
 * <Disclosure label="More cost views" count="8 sections">
 *   {sections}
 * </Disclosure>
 * ```
 *
 * Accessibility: the trigger is a `<button>` with `aria-expanded` and
 * `aria-controls`; the panel is a `<section role="region">` labelled by the
 * trigger. Keyboard: Enter/Space toggle (native), Escape closes from inside
 * and returns focus to the trigger. The chevron is decorative.
 */
export function Disclosure({
  label,
  count,
  hint,
  defaultOpen = false,
  openInFull = true,
  open: openProp,
  onOpenChange,
  children,
  className,
}: DisclosureProps) {
  const { mode } = useDemoMode();
  // `null` until the reader toggles the band; until then the mode decides.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = openProp ?? toggled ?? (defaultOpen || (openInFull && mode === 'full'));
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setToggled(next);
    onOpenChange?.(next);
  };
  const id = useId();
  const panelId = `${id}-panel`;
  const triggerId = `${id}-trigger`;

  return (
    <div
      className={cn('min-w-0 overflow-hidden rounded-xl border', className)}
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return;
        event.stopPropagation();
        setOpen(false);
        document.getElementById(triggerId)?.focus();
      }}
    >
      <button
        type="button"
        id={triggerId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
      >
        <ChevronRight
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-150',
            open && 'rotate-90',
          )}
          style={{ color: 'var(--ink-tertiary)' }}
        />
        <span className="min-w-0 flex-1">
          <span
            className="block text-[13px] font-semibold tracking-tight"
            style={{ color: 'var(--ink-primary)' }}
          >
            {label}
            {count ? (
              <span className="font-normal" style={{ color: 'var(--ink-tertiary)' }}>
                {' · '}
                {count}
              </span>
            ) : null}
          </span>
          {hint ? (
            <span
              className="mt-0.5 block text-[11.5px] leading-[1.5]"
              style={{ color: 'var(--ink-tertiary)' }}
            >
              {hint}
            </span>
          ) : null}
        </span>
        <span
          className="shrink-0 text-[11px] font-medium"
          style={{ color: 'var(--accent)' }}
        >
          {open ? 'Hide' : 'Show'}
        </span>
      </button>
      {open ? (
        <section
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          className="space-y-6 border-t px-4 py-5"
          style={{ borderColor: 'var(--hairline)', background: 'var(--surface-1)' }}
        >
          {children}
        </section>
      ) : null}
    </div>
  );
}

export default Disclosure;
